import moment from 'moment';
import Abstract from './Abstract.js';

export default class RowMilestones extends Abstract {
  constructor(props) {
    super(props);
    this.marker = null;
    this.markerWidth = 24;
    this.text = null;
  }

  build() {
    const { chart, milestones, options } = this.props;

    const groupMilestonesByDate = (milestones) => {
      const groupedMilestones = {};

      milestones.forEach((milestone) => {
        const dateAt = new Date(milestone.date_at);
        const dateKey = dateAt.toISOString().split('T')[0];

        if (!groupedMilestones[dateKey]) {
          groupedMilestones[dateKey] = [];
        }

        groupedMilestones[dateKey].push({
          ...milestone,
          projectId: options.projectId,
        });
      });

      return Object.entries(groupedMilestones).map(
        ([dateAt, childMilestone]) => ({
          date_at: dateAt,
          childMilestone,
        })
      );
    };

    const groupedMilestones = groupMilestonesByDate(milestones);
    const group = chart
      .selectAll('svg')
      .data(
        groupedMilestones.map((mile) => {
          const startDate = moment(mile.date_at).set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          });

          const endDate = moment(mile.date_at).set({
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 999,
          });
          return {
            ...mile,
            title: null,
            startDate,
            endDate,
          };
        })
      )
      .enter()
      .append('g')
      .attr('class', 'mile-count group-mile');

    this.marker = group
      .append('rect')
      .attr('class', 'mile modal-mile')
      .attr('rx', 8)
      .attr('ry', 8)
      .attr('cursor', 'pointer')
      .attr('height', 14)
      .attr('width', this.markerWidth)
      .attr('x', (d) => this.getVisibleStartX(d))
      .attr('y', 48);

    this.text = group
      .append('text')
      .text((d) => d.childMilestone.length)
      .attr('class', 'modal-mile')
      .style('fill', 'white')
      .style('font-size', '12px')
      .attr('dominant-baseline', 'central')
      .attr('cursor', 'pointer')
      .attr('x', '50%')
      .attr('y', '50%')
      .attr('x', (d) => this.getTextVisibleStartX(d))
      .attr('y', 55)
      .attr('opacity', (d) => (this.isLabelOverflowing(d) ? 0 : 1));
  }

  update() {
    this.marker
      .transition()
      .duration(this.transitionDuration)
      .attr('x', (d) => this.getVisibleStartX(d));
    this.text
      .transition()
      .duration(this.transitionDuration)
      .attr('x', (d) => this.getTextVisibleStartX(d));
  }

  getStartX(datum) {
    return this.xScale(datum.startDate);
  }

  getEndX(datum) {
    return this.xScale(datum.endDate);
  }

  getVisibleStartX(datum) {
    return (
      this.getStartX(datum) + (this.getWidth(datum) - this.markerWidth) / 2
    );
  }

  getTextVisibleStartX(datum) {
    const textWidth = this.getEstimatedTextWidth(
      String(datum.childMilestone.length)
    );
    const shift = (this.markerWidth - textWidth) / 2 - 1;
    return (
      this.getStartX(datum) +
      (this.getWidth(datum) - this.markerWidth) / 2 +
      shift
    );
  }

  getVisibleEndX(datum) {
    return Math.min(this.rangeEnd, this.getEndX(datum));
  }

  getWidth(datum) {
    return this.getEndX(datum) - this.getStartX(datum);
  }

  getVisibleWidth(datum) {
    return this.getVisibleEndX(datum) - this.getVisibleStartX(datum);
  }

  getEstimatedTextWidth(datum) {
    return datum.length * 6;
  }

  isLabelOverflowing(datum) {
    return this.getEstimatedTextWidth(datum) > this.getVisibleWidth(datum);
  }
}
